import React, { useState } from 'react';
import { Modal, Banner, Button, ProgressBar, Text } from '@shopify/polaris';
import { useAuthFetch } from './AuthComponent';

/**
 * Bulk "Mark as stocked" confirm → progress → summary modal.
 *
 * Activates every tracked variant of the selected products at the user's
 * current location WITHOUT changing on-hand quantities. Already-fully-stocked
 * products are skipped server-side. The bulk path is irreversible-in-bulk so
 * the primary action is gated behind typing literal "ENABLE".
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - mode: 'query' | 'ids'  — drives the request payload
 *  - q, searchBy            — for mode='query'
 *  - productIds: string[]   — for mode='ids' (the selected page or selection set)
 *  - totalCount, variantTotal, skipCount  — display numbers for the confirm page
 *  - location: string       — display name
 *  - onComplete: ({ successIds, totals, failures }) => void  — parent flashes rows + shows banner
 */
export default function StockBulkModal({
    open, onClose, mode, q, searchBy, productIds,
    totalCount, variantTotal, skipCount, location, onComplete,
}) {
    const storeFetch = useAuthFetch();
    const [phase, setPhase] = useState('confirm'); // 'confirm' | 'running' | 'summary'
    const [typed, setTyped] = useState('');
    const [progress, setProgress] = useState(0);
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState(null);

    const canConfirm = typed.trim().toUpperCase() === 'ENABLE';

    const reset = () => {
        setPhase('confirm'); setTyped(''); setProgress(0);
        setSummary(null); setError(null);
    };

    const handleClose = () => {
        if (phase === 'running') return;  // can't dismiss mid-run
        reset();
        onClose && onClose();
    };

    const run = async () => {
        setPhase('running');
        setProgress(8);
        // The endpoint is single round-trip; the bar animates while we wait
        // so the user sees motion, then snaps to 100 on response.
        const tick = setInterval(() => {
            setProgress(p => (p < 90 ? p + 2 : p));
        }, 240);
        try {
            const body = mode === 'query'
                ? { mode: 'query', q, search_by: searchBy }
                : { mode: 'ids', product_ids: productIds || [] };
            const res = await storeFetch('/api/products/bulk-stock', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
            clearInterval(tick);
            setProgress(100);
            setSummary(data);
            setPhase('summary');
        } catch (e) {
            clearInterval(tick);
            setError(e.message || 'Bulk stock failed');
            setPhase('confirm');
            setProgress(0);
        }
    };

    const finish = () => {
        const successIds = (summary?.results || [])
            .filter(r => r.ok && !r.skipped && (r.variants_activated || 0) > 0)
            .map(r => r.product_id);
        const failures = (summary?.results || []).filter(r => !r.ok);
        onComplete && onComplete({
            successIds,
            totals: summary?.totals || {},
            failures,
        });
        reset();
        onClose && onClose();
    };

    // ── Confirm phase ────────────────────────────────────────────────────
    if (phase === 'confirm') {
        return (
            <Modal
                open={open}
                onClose={handleClose}
                title="Mark products as stocked"
                primaryAction={{
                    content: `Mark ${totalCount} ${totalCount === 1 ? 'product' : 'products'} as stocked`,
                    destructive: true,
                    disabled: !canConfirm,
                    onAction: run,
                }}
                secondaryActions={[{ content: 'Cancel', onAction: handleClose }]}
            >
                <Modal.Section>
                    {error && (
                        <Banner tone="critical" onDismiss={() => setError(null)}>
                            {error}
                        </Banner>
                    )}
                    <Banner tone="warning">
                        <p>
                            You are marking <strong>all {totalCount} {totalCount === 1 ? 'product' : 'products'}</strong>{' '}
                            in the results as stocked at <strong>{location}</strong>. This cannot be reversed in bulk —
                            each product would have to be unstocked individually.
                        </p>
                    </Banner>

                    <div className="sp-confirm-stat">
                        <div className="sp-confirm-stat__box">
                            <div className="sp-confirm-stat__num">{totalCount}</div>
                            <div className="sp-confirm-stat__lbl">
                                {totalCount === 1 ? 'product' : 'products'} selected
                            </div>
                        </div>
                        <div className="sp-confirm-stat__box">
                            <div className="sp-confirm-stat__num">{variantTotal}</div>
                            <div className="sp-confirm-stat__lbl">variants will be activated</div>
                        </div>
                        <div className="sp-confirm-stat__box">
                            <div className="sp-confirm-stat__num sp-confirm-stat__num--loc">{location}</div>
                            <div className="sp-confirm-stat__lbl">target location</div>
                        </div>
                    </div>

                    <p className="sp-inline-muted">
                        On-hand quantities are left unchanged — products are simply made available to sell at this location.
                        {skipCount > 0 && <> {skipCount} already fully stocked {skipCount === 1 ? 'product' : 'products'} will be skipped.</>}
                    </p>

                    <div className="sp-confirm-type">
                        <label htmlFor="sp-enable-confirm">
                            Type <strong>ENABLE</strong> to confirm
                        </label>
                        <input
                            id="sp-enable-confirm"
                            value={typed}
                            onChange={e => setTyped(e.target.value)}
                            placeholder="ENABLE"
                            autoFocus
                            spellCheck={false}
                            autoComplete="off"
                        />
                    </div>
                </Modal.Section>
            </Modal>
        );
    }

    // ── Running phase ────────────────────────────────────────────────────
    if (phase === 'running') {
        return (
            <Modal
                open={open}
                onClose={() => {}}
                title="Marking products as stocked…"
            >
                <Modal.Section>
                    <Text as="p">
                        Activating at <strong>{location}</strong>. Please keep this open.
                    </Text>
                    <div style={{ marginTop: 14 }}>
                        <ProgressBar progress={progress} size="medium" />
                    </div>
                    <div className="sp-progress-text">{progress}% complete</div>
                </Modal.Section>
            </Modal>
        );
    }

    // ── Summary phase ────────────────────────────────────────────────────
    const totals = summary?.totals || {};
    const failures = (summary?.results || []).filter(r => !r.ok);
    const hasFailures = failures.length > 0;
    return (
        <Modal
            open={open}
            onClose={finish}
            title="Bulk stock complete"
            primaryAction={{ content: 'Done', onAction: finish }}
        >
            <Modal.Section>
                <Banner tone={hasFailures ? 'warning' : 'success'}>
                    <p>
                        Enabled <strong>{totals.enabled || 0}</strong>{' '}
                        {(totals.enabled || 0) === 1 ? 'product' : 'products'}{' '}
                        (<strong>{totals.variants_enabled || 0}</strong> variants) at <strong>{location}</strong>.
                        {(totals.skipped || 0) > 0 && (
                            <> {totals.skipped} already stocked {totals.skipped === 1 ? 'product was' : 'products were'} skipped.</>
                        )}
                        {hasFailures && <> {failures.length} failed and need manual review.</>}
                    </p>
                </Banner>

                {hasFailures && (
                    <>
                        <p className="sp-inline-muted" style={{ margin: '10px 0 6px' }}>
                            These need manual review — they were left unchanged:
                        </p>
                        <ul className="sp-fail-list">
                            {failures.map(f => (
                                <li key={f.product_id}>
                                    <span>{f.title || f.product_id}</span>
                                    <span className="sp-fail-reason">{f.reason || 'unknown error'}</span>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </Modal.Section>
        </Modal>
    );
}
